import { HttpAction, HttpService } from "@megaron/http-service";
import { Serializers, SerializerValue } from "@megaron/serializers";

import { unitTemplateCategories } from "./unitTemplate";

export const calculatorTemplateSerializer = Serializers.object({
  id: Serializers.string,
  unit: Serializers.string,
  category: Serializers.stringOneOf(...unitTemplateCategories),
  label: Serializers.string,
  complexity: Serializers.integer,
  isMandatory: Serializers.boolean,
  components: Serializers.record(Serializers.integer),
});
export type CalculatorTemplate = SerializerValue<typeof calculatorTemplateSerializer>;

export const calculatorObjectives = [
  "min_unallocated",
  "max_mandatory_templates",
  "min_cost",
  "min_complexity",
  "min_count",
] as const;
export type CalculatorObjective = (typeof calculatorObjectives)[number];
export const defaultCalculatorObjectives = [
  "min_unallocated",
  "max_mandatory_templates",
  "min_cost",
  "min_complexity",
] satisfies CalculatorObjective[];
export const calculatorObjectiveSerializer = Serializers.stringOneOf<CalculatorObjective>(...calculatorObjectives);

export const calculatorContextSerializer = Serializers.object({
  templates: calculatorTemplateSerializer.array(),
  inputUnits: Serializers.record(Serializers.integer),
  topCosts: Serializers.record(Serializers.integer),
  fillerCosts: Serializers.record(Serializers.integer),
  inputCosts: Serializers.record(Serializers.integer),
  extraUnitAllowance: Serializers.integer,
  objectives: calculatorObjectiveSerializer.array(),
});
export type CalculatorContext = SerializerValue<typeof calculatorContextSerializer>;

export type MaterializedTopUnit = {
  topUnit: string;
  inputs: Partial<Record<string, number>>;
  filler: Partial<Record<string, number>>;
  templateUses: Partial<Record<string, number>>;
};

export const materializedTopUnitSerializer = Serializers.object<MaterializedTopUnit>({
  topUnit: Serializers.string,
  inputs: Serializers.record(Serializers.integer),
  filler: Serializers.record(Serializers.integer),
  templateUses: Serializers.record(Serializers.integer),
});

export type CalculatorOutput = {
  unallocated: Partial<Record<string, number>>;
  topUnits: MaterializedTopUnit[];
};

export const calculatorOutputSerializer = Serializers.object<CalculatorOutput>({
  unallocated: Serializers.record(Serializers.integer),
  topUnits: materializedTopUnitSerializer.array(),
});

export const costComponentSerializer = Serializers.object({
  type: Serializers.stringOneOf("input", "top", "filler"),
  cost: Serializers.decimal,
  unit: Serializers.string,
  unitCost: Serializers.decimal,
  quantity: Serializers.integer,
});
export type CostComponent = SerializerValue<typeof costComponentSerializer>;

export const solutionSerializer = Serializers.object({
  totalCost: Serializers.decimal,
  topCost: Serializers.decimal,
  fillerCost: Serializers.decimal,
  inputCost: Serializers.decimal,
  costBreakdown: costComponentSerializer.array(),
  unallocated: Serializers.record(Serializers.integer),
  topUnits: Serializers.object({
    unit: Serializers.string,
    quantity: Serializers.integer,
    cost: Serializers.decimal,
    topCost: Serializers.decimal,
    fillerCost: Serializers.decimal,
    inputCost: Serializers.decimal,
    inputs: Serializers.record(Serializers.integer),
    filler: Serializers.record(Serializers.integer),
    templateUses: Serializers.record(Serializers.integer),
    fillerCosts: Serializers.record(Serializers.decimal),
  }).array(),
});

export type ShippingSolution = SerializerValue<typeof solutionSerializer>;

export const calculateInputSerializer = Serializers.object({
  items: Serializers.object({
    itemId: Serializers.string,
    quantity: Serializers.integer,
  }).array(),
  shippingRates: Serializers.record(Serializers.decimal),
  fillerCosts: Serializers.record(Serializers.decimal),
  inputCosts: Serializers.record(Serializers.decimal),
  extraUnitAllowance: Serializers.integer.optional(),
  objectives: calculatorObjectiveSerializer.array().optional(),
});
export type CalculateInput = SerializerValue<typeof calculateInputSerializer>;

export const calculateActions = HttpService({
  calculate: HttpAction({
    method: "post",
    path: "/calculate",
    bodySerializer: calculateInputSerializer,
    errorSerializer: Serializers.stringOneOf("NoSolution"),
    valueSerializer: solutionSerializer,
  }),
});
